import { IOrder, IShipment } from '@/types'
import { Sale } from 'components/common/SalesTable'
import { SendcloudStatusesMap } from 'components/my-account/ShipmentStatus'
import { Currency } from 'constants/currencies'
import { Order } from 'utils/data'
import { Err, getApiBasepath } from '.'

export type OrderPaymentStatus =
  | 'PENDING'
  | 'PENDING_AUTHENTIFICATION'
  | 'PAID'
  | 'FAILED'
  | 'CANCELLED_PAYMENT_FAILED'
  | 'CANCELLED_SHIPMENT'
  | 'CANCELLED_OTHER'

export type OrderPayoutStatus = 'PENDING' | 'FAILED' | 'SUCCESS'

export type OrderShipmentStatus = keyof typeof SendcloudStatusesMap

export type UserOrder = {
  id?: number
  createdAt?: string
  updatedAt?: string
  payoutStatus?: OrderPayoutStatus
  paymentStatus?: OrderPaymentStatus
  orderPaidAt?: string
  payoutDoneAt?: string
  payoutDate?: string
  price?: number
  priceAsk?: number
  bid?: {
    id: number
  }
  shipment?: {
    id?: number
    trackingNumber?: string
    status?: OrderShipmentStatus
    shippingDeadline?: string
    url?: string | null
    upsShipment?: boolean
  }
}

type GetOrderRes = Order

type GetSalesRes = Sale[]

type GetOrdersRes = {
  data: IOrder[]
}

export type Shipment = {
  id: number
  status?: OrderShipmentStatus
  trackingNumber: string
  url: string | null
  upsShipment?: boolean
  label: {
    id: number
    url: string
  }
}

type ValidateAddressRes = {
  data?: {
    address: { cityName: string; countryCode: string; postalCode: string }[]
  }
  error?: Err
}

export const getOrder = async (id: number, query?: string) => {
  const response = await fetch(
    `${getApiBasepath()}/api/orders/${id}?${query}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )
  const data = await response.json()
  return data as GetOrderRes
}

export const getOrders = async (query?: string) => {
  const response = await fetch(`${getApiBasepath()}/api/orders?${query}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  })
  const data = await response.json()
  return data as GetOrdersRes
}

export const getSales = async (
  payload: {
    artworkId?: number | null
    maxResults?: number | null
    editionName?: string | null
    userCurrency?: Currency
    userCountry?: string
  } = { artworkId: null, maxResults: null, editionName: null }
) => {
  const headers = {
    'Content-Type': 'application/json',
  } as { 'Content-Type': string; Authorization?: string }
  const token = localStorage.getItem('token')
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  const response = await fetch(`${getApiBasepath()}/api/orders/sales`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers,
  })
  const data = await response.json()
  return data as GetSalesRes
}

export const getShipment = async (orderId: number) => {
  const response = await fetch(
    `${getApiBasepath()}/api/shipments?filters[order][id]=${orderId}&populate[0]=label`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )
  const data = await response.json()

  const shipment: IShipment = data?.data?.[0]
  if (!shipment) return

  return {
    id: shipment.id,
    ...shipment.attributes,
    label: shipment?.attributes?.label?.data
      ? {
          id: shipment.attributes.label.data?.id,
          ...shipment.attributes.label.data.attributes,
        }
      : null,
  } as Shipment
}

export const validateAddress = async (
  countryCode: string,
  postalCode: string
) => {
  const response = await fetch(
    `${getApiBasepath()}/api/orders/validate-address/${encodeURIComponent(
      countryCode
    )}/${encodeURIComponent(postalCode)}`
  )
  const data = await response.json()
  return data as ValidateAddressRes
}
